/**
 * The wetalk app's consent page — `/app-sign-in` (plan-034 WAPP-APPSIGNIN; design §45.4 steps 2–5; ADR 0089;
 * FR-MOB-AUTH-2, FR-MOB-AUTH-9, FR-MOB-AUTH-10).
 *
 * The app opens this page in the phone's browser with its S256 challenge, a `state` and the platform
 * (`./app-sign-in-request.ts` judges the four values; anything else is one page that names no value).
 *
 *   · NOT SIGNED IN — the four values are held for the tab and the visitor goes through the portal's EXISTING sign-in
 *     (`/log-in`: password, emailed code, Google, Microsoft, single sign-on, the second step, the identity challenge),
 *     unchanged. The shell's guard brings them back here with the same values; the sign-in was made for the app.
 *   · SIGNED IN — the page names the person and the device and offers two things:
 *       "Continue"             mints the app authorization code with this browser's cookie and CSRF token
 *                              (`createAppAuthorizationCode`), then sends the browser to the api's return address with
 *                              `code` and `state` — and on Android draws "Return to wetalk" as a real link at once (a
 *                              navigation that outlives the tap may be blocked by Chrome; the code lives 60 seconds);
 *       "Use another account"  ends this browser sign-in and starts again through `/log-in`.
 *     A person with no organization or no workspace consents just the same: the app shows its own "nothing to show".
 *   · `ends_browser_auth_session` is D-25's fact: `true` only when the browser sign-in was made on the way, for this
 *     request; a portal sign-in that already existed in the phone's browser is kept.
 *
 * Refusals, each in plain words: the switch off (`422 app_sign_in_unavailable` — "not available yet"), an operator
 * acting as the person (`403 app_sign_in_refused`, also told before anything is asked), the organization's
 * authenticator (`401 assurance_required` — the second step here, then the mint again), its single sign-on, and a
 * network outside its allowed ones (`403 network_not_allowed`). The code is never written to a log, a query cache or
 * storage; it exists only in the address the browser is sent to.
 *
 * Drawn in the public frame of the way-in pages (`../auth/public-frame.tsx`). No `data-screen-label`: the prototype
 * has no screen for it.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ApiError } from "@wetalk/api-client";
import { Button } from "@wetalk/ui";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { AuthSessionView } from "../../api/gateway.js";
import { useGateway } from "../../api/provider.js";
import { QUERY_KEY, authContextQuery } from "../../api/query.js";
import "../../api/slice/app-sign-in.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
import { PublicFrame, PublicKicker } from "../auth/public-frame.js";

import {
  APP_SIGN_IN_EXIT,
  appSignInHoldOf,
  appSignInRequestOf,
  appSignInTargetOf,
  clearAppSignInHold,
  holdAppSignIn,
  madeForAppSignIn,
  tabStorage,
  tabStorageKeeps,
  type AppSignInRequest,
  type AppSignInSearch,
} from "./app-sign-in-request.js";
import { AppSignInSecondStep } from "./app-sign-in-second-step.js";

/** What the last "Continue" came to. */
type Outcome =
  | { readonly kind: "returning"; readonly target: string }
  | { readonly kind: "unavailable" }
  | { readonly kind: "refused" }
  | { readonly kind: "second_step" }
  | { readonly kind: "single_sign_on" }
  | { readonly kind: "network" }
  | { readonly kind: "unusable" }
  | { readonly kind: "failed" };

/** The card's frame: the "wetalk app" kicker above every state. */
function Card(props: { readonly children: ReactNode }): ReactNode {
  usePortalTranslation("appSignIn");
  return (
    <PublicFrame>
      <div className="pd-form">
        <PublicKicker>{portalText("appSignIn", "kicker")}</PublicKicker>
        {props.children}
      </div>
    </PublicFrame>
  );
}

function Said(props: { readonly title: string; readonly lede: string; readonly children?: ReactNode }): ReactNode {
  return (
    <>
      <h1 tabIndex={-1}>{props.title}</h1>
      <p className="pd-lede">{props.lede}</p>
      {props.children}
    </>
  );
}

function outcomeOf(raised: unknown): Outcome | "signed_out" {
  if (!(raised instanceof ApiError)) return { kind: "failed" };
  switch (raised.code) {
    case "app_sign_in_unavailable":
      return { kind: "unavailable" };
    case "app_sign_in_refused":
      return { kind: "refused" };
    case "assurance_required":
      return raised.field === "single_sign_on" ? { kind: "single_sign_on" } : { kind: "second_step" };
    case "network_not_allowed":
      return { kind: "network" };
    case "validation_failed":
      return { kind: "unusable" };
    case "authentication_required":
    case "auth_session_expired":
    case "auth_session_revoked":
      return "signed_out";
    default:
      return { kind: "failed" };
  }
}

export function AppSignInScreen(props: { readonly search: AppSignInSearch }): ReactNode {
  usePortalTranslation("appSignIn");
  const request = useMemo(() => appSignInRequestOf(props.search), [props.search]);
  if (request === null) {
    return (
      <Card>
        <Said title={portalText("appSignIn", "unusable.title")} lede={portalText("appSignIn", "unusable.lede")} />
      </Card>
    );
  }
  return <Consent request={request} />;
}

function Consent({ request }: { readonly request: AppSignInRequest }): ReactNode {
  usePortalTranslation("appSignIn");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const context = useQuery(authContextQuery(gateway));
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  /** A tab that keeps nothing cannot bring the person back after `/log-in`: they are told so instead. */
  const [heldForTab] = useState(() => tabStorageKeeps(tabStorage()));

  /**
   * D-25's fact, settled once per page: the hold names this request, so the sign-in in use was made for the app. On
   * arrival the hold is marked `arrived` (the shell's guard no longer sends the portal back here); a hold for another
   * request is stale and goes.
   */
  const [madeForApp] = useState(() => {
    const storage = tabStorage();
    const hold = appSignInHoldOf(storage, Date.now());
    if (hold === null) return false;
    if (!madeForAppSignIn(hold, request)) {
      clearAppSignInHold(storage);
      return false;
    }
    return true;
  });

  const signedIn: AuthSessionView | null | undefined = context.data;

  // Signed in: the hold, if any, has arrived. Signed out: hold the request and go through the existing sign-in.
  const sentToSignIn = useRef(false);
  useEffect(() => {
    if (signedIn === undefined) return;
    const storage = tabStorage();
    if (signedIn !== null) {
      const hold = appSignInHoldOf(storage, Date.now());
      if (hold !== null && hold.phase === "signing_in" && madeForAppSignIn(hold, request)) holdAppSignIn(storage, request, "arrived", hold.heldAtMs);
      return;
    }
    if (sentToSignIn.current || !heldForTab) return;
    if (!holdAppSignIn(storage, request, "signing_in", Date.now())) return;
    sentToSignIn.current = true;
    void navigate({ to: "/log-in", replace: true });
  }, [signedIn, request, navigate, heldForTab]);

  /** Back through the sign-in: this sign-in no longer stands for the app. */
  const signInAgain = (): void => {
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== QUERY_KEY.authSession[0] });
    queryClient.setQueryData<AuthSessionView | null>(QUERY_KEY.authSession, null);
  };

  const mint = useMutation({
    mutationFn: () =>
      gateway.createAppAuthorizationCode({
        codeChallenge: request.codeChallenge,
        devicePlatform: request.devicePlatform,
        endsBrowserAuthSession: madeForApp,
      }),
    onSuccess: (minted) => {
      clearAppSignInHold(tabStorage());
      const target = appSignInTargetOf(minted.returnUri, minted.code, request.state, window.location.href);
      setOutcome({ kind: "returning", target });
      APP_SIGN_IN_EXIT.leave(target);
    },
    onError: (raised: unknown) => {
      const next = outcomeOf(raised);
      if (next === "signed_out") {
        setOutcome(null);
        signInAgain();
        return;
      }
      setOutcome(next);
    },
  });

  const anotherAccount = useMutation({
    mutationFn: () => gateway.logOut(),
    onSuccess: () => {
      setOutcome(null);
      sentToSignIn.current = false;
      signInAgain();
    },
    onError: () => {
      setOutcome({ kind: "failed" });
    },
  });

  const anotherAccountButton = (
    <Button variant="secondary" block disabled={anotherAccount.isPending} onClick={() => anotherAccount.mutate()}>
      {portalText("appSignIn", anotherAccount.isPending ? "consent.signing_out" : "consent.another_account")}
    </Button>
  );

  if (!heldForTab && signedIn === null) {
    return (
      <Card>
        <Said title={portalText("appSignIn", "no_storage.title")} lede={portalText("appSignIn", "no_storage.lede")}>
          <a className="btn btn-primary btn-block" href="/log-in">{portalText("appSignIn", "no_storage.log_in")}</a>
        </Said>
      </Card>
    );
  }
  if (context.isError) {
    return (
      <Card>
        <Said title={portalText("appSignIn", "failed.title")} lede={portalText("appSignIn", "failed.lede")}>
          <Button variant="primary" block onClick={() => void context.refetch()}>{portalText("appSignIn", "failed.try_again")}</Button>
        </Said>
      </Card>
    );
  }
  if (signedIn === undefined) {
    return (
      <Card>
        <p className="pd-lede" role="status">{portalText("appSignIn", "loading")}</p>
      </Card>
    );
  }
  if (signedIn === null) {
    return (
      <Card>
        <p className="pd-lede" role="status">{portalText("appSignIn", "to_sign_in")}</p>
      </Card>
    );
  }

  // An operator acting as the person never gets an app sign-in (FR-MOB-AUTH-9); the api refuses it too.
  if (signedIn.actor.kind === "operator" || outcome?.kind === "refused") {
    return (
      <Card>
        <Said
          title={portalText("appSignIn", "refused.title")}
          lede={portalText("appSignIn", signedIn.actor.kind === "operator" ? "refused.operator" : "refused.other")}
        >
          {anotherAccountButton}
        </Said>
      </Card>
    );
  }

  switch (outcome?.kind) {
    case "returning":
      return (
        <Card>
          <Said title={portalText("appSignIn", "returning.title")} lede={portalText("appSignIn", "returning.lede")}>
            {request.devicePlatform === "android" ? (
              <>
                <p className="c-entry-code-note">{portalText("appSignIn", "returning.android_lede")}</p>
                <a className="btn btn-primary btn-block" href={outcome.target}>{portalText("appSignIn", "returning.link")}</a>
              </>
            ) : null}
            <p className="pd-fine">{portalText("appSignIn", "returning.expiry")}</p>
          </Said>
        </Card>
      );
    case "unavailable":
      return (
        <Card>
          <Said title={portalText("appSignIn", "unavailable.title")} lede={portalText("appSignIn", "unavailable.lede")} />
        </Card>
      );
    case "unusable":
      return (
        <Card>
          <Said title={portalText("appSignIn", "unusable.title")} lede={portalText("appSignIn", "unusable.lede")} />
        </Card>
      );
    case "network":
      return (
        <Card>
          <Said title={portalText("appSignIn", "network.title")} lede={portalText("appSignIn", "network.lede")} />
        </Card>
      );
    case "single_sign_on":
      return (
        <Card>
          <Said title={portalText("appSignIn", "single_sign_on.title")} lede={portalText("appSignIn", "single_sign_on.lede")}>
            {anotherAccountButton}
          </Said>
        </Card>
      );
    case "second_step":
      return (
        <Card>
          <AppSignInSecondStep onDone={() => mint.mutate()} />
        </Card>
      );
    default:
      break;
  }

  return (
    <Card>
      <h1 tabIndex={-1}>{portalText("appSignIn", "consent.title")}</h1>
      <p className="pd-lede">
        {portalText("appSignIn", request.devicePlatform === "ios" ? "consent.lede_ios" : "consent.lede_android", { emailAddress: signedIn.person.emailAddress })}
      </p>
      <p className="c-entry-code-note">{portalText("appSignIn", "consent.note")}</p>
      {outcome?.kind === "failed" ? (
        <p className="wt-field-error" role="alert">{portalText("appSignIn", "failed.lede")}</p>
      ) : null}
      <Button variant="primary" block disabled={mint.isPending || anotherAccount.isPending} onClick={() => mint.mutate()}>
        {portalText("appSignIn", mint.isPending ? "consent.continuing" : "consent.continue")}
      </Button>
      {anotherAccountButton}
    </Card>
  );
}
